import { useMemo, useState, useEffect } from "react";
import { DateTime } from "luxon";
import { computeDayun, computeLiunian } from "../utils/computeBaziPillars";

// Import Wu Xing images
import woodIcon from '../styles/wood.png';
import fireIcon from '../styles/fire.png';
import earthIcon from '../styles/earth.png';
import goldIcon from '../styles/gold.png';
import waterIcon from '../styles/water.png';

// Wu Xing images mapping
const WUXING_IMAGES = {
  wood: woodIcon,
  fire: fireIcon,
  earth: earthIcon,
  metal: goldIcon,
  water: waterIcon
};

const STEMS_WUXING = {
  '甲': 'wood', '乙': 'wood', '丙': 'fire', '丁': 'fire', 
  '戊': 'earth', '己': 'earth', '庚': 'metal', '辛': 'metal', 
  '壬': 'water', '癸': 'water', 'Jia': 'wood', 'Yi': 'wood', 
  'Bing': 'fire', 'Ding': 'fire', 'Wù': 'earth', 'Ji': 'earth', 
  'Geng': 'metal', 'Xin': 'metal', 'Ren': 'water', 'Gui': 'water'
};

const BRANCHES_WUXING = {
  '子': 'water', '丑': 'earth', '寅': 'wood', '卯': 'wood', 
  '辰': 'earth', '巳': 'fire', '午': 'fire', '未': 'earth', 
  '申': 'metal', '酉': 'metal', '戌': 'earth', '亥': 'water',
  'Zi': 'water', 'Chou': 'earth', 'Yin': 'wood', 'Mao': 'wood', 
  'Chen': 'earth', 'Si': 'fire', 'Wǔ': 'fire', 'Wei': 'earth', 
  'Shen': 'metal', 'You': 'metal', 'Xu': 'earth', 'Hai': 'water'
};

const STEMS_EN = ["Jia", "Yi", "Bing", "Ding", "Wù", "Ji", "Geng", "Xin", "Ren", "Gui"];
const STEMS_ZH = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
const BRANCHES_EN = ["Zi", "Chou", "Yin", "Mao", "Chen", "Si", "Wǔ", "Wei", "Shen", "You", "Xu", "Hai"];
const BRANCHES_ZH = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];

const getStemName = (val, lang) => {
  if (!val) return '';
  let index = STEMS_EN.indexOf(val);
  if (index === -1) index = STEMS_ZH.indexOf(val);
  if (index === -1) return val;
  return lang === 'zh' ? STEMS_ZH[index] : STEMS_EN[index];
};

const getBranchName = (val, lang) => {
  if (!val) return '';
  let index = BRANCHES_EN.indexOf(val);
  if (index === -1) index = BRANCHES_ZH.indexOf(val);
  if (index === -1) return val;
  return lang === 'zh' ? BRANCHES_ZH[index] : BRANCHES_EN[index];
};

const STEMS_YINYANG = {
  '甲': 'yang', '乙': 'yin', '丙': 'yang', '丁': 'yin',
  '戊': 'yang', '己': 'yin', '庚': 'yang', '辛': 'yin',
  '壬': 'yang', '癸': 'yin', 'Jia': 'yang', 'Yi': 'yin',
  'Bing': 'yang', 'Ding': 'yin', 'Wù': 'yang', 'Ji': 'yin',
  'Geng': 'yang', 'Xin': 'yin', 'Ren': 'yang', 'Gui': 'yin'
};

// const BRANCHES_YINYANG = {
//   '子': 'yang', '丑': 'yin', '寅': 'yang', '卯': 'yin',
//   '辰': 'yang', '巳': 'yin', '午': 'yang', '未': 'yin',
//   '申': 'yang', '酉': 'yin', '戌': 'yang', '亥': 'yin',
//   'Zi': 'yang', 'Chou': 'yin', 'Yin': 'yang', 'Mao': 'yin',
//   'Chen': 'yang', 'Si': 'yin', 'Wǔ': 'yang', 'Wei': 'yin',
//   'Shen': 'yang', 'You': 'yin', 'Xu': 'yang', 'Hai': 'yin'
// };

const BRANCH_HIDDEN_STEMS = {
  '子': ['癸'], '丑': ['己', '癸', '辛'], '寅': ['甲', '丙', '戊'],
  '卯': ['乙'], '辰': ['戊', '乙', '癸'], '巳': ['丙', '庚', '戊'],
  '午': ['丁', '己'], '未': ['己', '丁', '乙'], '申': ['庚', '壬', '戊'],
  '酉': ['辛'], '戌': ['戊', '辛', '丁'], '亥': ['壬', '甲'],
  'Zi': ['Gui'], 'Chou': ['Ji', 'Gui', 'Xin'], 'Yin': ['Jia', 'Bing', 'Wù'],
  'Mao': ['Yi'], 'Chen': ['Wù', 'Yi', 'Gui'], 'Si': ['Bing', 'Geng', 'Wù'],
  'Wǔ': ['Ding', 'Ji'], 'Wei': ['Ji', 'Ding', 'Yi'], 'Shen': ['Geng', 'Ren', 'Wù'],
  'You': ['Xin'], 'Xu': ['Wù', 'Xin', 'Ding'], 'Hai': ['Ren', 'Jia']
};

const ELEMENT_COLORS = {
  wood: '#1ba000ff',
  fire: '#d80000ff',
  earth: '#6f5851ff',
  metal: '#eea600ff',
  water: '#008afbff'
};

const ELEMENT_MEANINGS = {
  '甲': { 
    en: 'Yang Wood (Jia): Represents towering trees, sturdy timber, and resilience. It symbolizes growth, uprightness, and leadership. Like a mighty oak, it is strong, reliable, and protective, but can be rigid or stubborn.', 
    zh: '阳木（甲）：代表参天大树、栋梁之材。象征生长、正直、仁慈和领导力。如松柏般坚强可靠，但也可能刚直易折，不知变通。' 
  },
  '乙': { 
    en: 'Yin Wood (Yi): Represents flowers, vines, and grass. It is flexible, adaptable, and persistent. Like ivy, it can survive by adjusting to its environment, networking, and finding ways around obstacles.', 
    zh: '阴木（乙）：代表花草、藤蔓。象征柔韧、适应力和毅力。如常春藤般能屈能伸，善于依附和生存，虽柔弱但生命力顽强。' 
  },
  '丙': { 
    en: 'Yang Fire (Bing): Represents the Sun. It radiates warmth, light, and energy to all without discrimination. It symbolizes passion, generosity, and openness, but can be impulsive, routine-averse, or overpowering.', 
    zh: '阳火（丙）：如太阳，普照万物。象征热情、慷慨、急躁和光明磊落。充满活力，喜怒形于色，但也可能冲动或过于耀眼灼人。' 
  },
  '丁': { 
    en: 'Yin Fire (Ding): Represents a candle, torch, or star. It provides focused illumination and guidance. It is meticulous, sensitive, and sacrificial, often burning itself to help others. It represents civilization and insight.', 
    zh: '阴火（丁）：如烛光、星火、灯盏。象征专注、指引和奉献。心思细腻，洞察力强，往往牺牲自己照亮他人，具有文明与智慧的特质。' 
  },
  '戊': { 
    en: 'Yang Earth (Wu): Represents mountains, rocks, and boulders. It is solid, trustworthy, and stable. It can be stubborn and slow to change, but offers great protection, reliability, and a strong foundation.', 
    zh: '阳土（戊）：如高山、巨石、堤岸。象征稳重、诚信、包容和厚重。性格固执不易改变，但给人极大的安全感，是值得信赖的依靠。' 
  },
  '己': { 
    en: 'Yin Earth (Ji): Represents garden soil or fertile land. It is nurturing, productive, and resourceful. It can be complex and holds many hidden talents, capable of growing anything, but can be possessive.', 
    zh: '阴土（己）：如田园、湿土。象征培育、包容和多才多艺。内心丰富，能生万物，适应力强，但也容易藏污纳垢，心思较重。' 
  },
  '庚': { 
    en: 'Yang Metal (Geng): Represents raw iron, axes, or swords. It is sharp, decisive, and justice-seeking. It endures hardship to become useful, symbolizing altruism, loyalty, and strong execution power.', 
    zh: '阳金（庚）：如刀剑、矿石、斧钺。象征刚毅、果断、义气和革新。需经火炼方成大器，执行力强，但也可能过于刚硬伤人。' 
  },
  '辛': { 
    en: 'Yin Metal (Xin): Represents jewelry, needles, or fine instruments. It is delicate, valuable, and seeks attention. It values aesthetics and "face," often sharp-tongued but soft-hearted, seeking perfection.', 
    zh: '阴金（辛）：如首饰、针尖、玉器。象征精致、贵气、爱面子和改革。外表柔美，内心坚韧，言辞犀利，追求完美和生活品质。' 
  },
  '壬': { 
    en: 'Yang Water (Ren): Represents the ocean or rushing river. It is powerful, dynamic, and intelligent. It represents wisdom and movement, adaptable and fast, but can be overwhelming, rebellious, or unfocused.', 
    zh: '阳水（壬）：如江河、大海。象征智慧、流动、奔放和冲劲。反应快，适应力强，善于变通，但也可能泛滥成灾，任性不羁。' 
  },
  '癸': { 
    en: 'Yin Water (Gui): Represents mist, rain, or dew. It is gentle, nurturing, and pervasive. It represents intuition, mystery, and strategy, able to infiltrate anywhere silently and influence subtly.', 
    zh: '阴水（癸）：如雨露、云雾、泉水。象征温柔、滋润、灵感和智谋。无孔不入，心思深沉，善于以柔克刚，情绪多变。' 
  },
  
  '子': { 
    en: 'Yang Water (Rat): Winter Solstice, Midnight. Represents wisdom, adaptability, and hidden movement. Contains pure Yin Water (Gui). Associated with charm and resourcefulness.', 
    zh: '阳水（子鼠）：冬至，午夜。象征智慧、机灵和潜藏。藏干为癸水。具有桃花性质，善于社交和应变。' 
  },
  '丑': { 
    en: 'Yin Earth (Ox): Late Winter, Frozen Ground. Represents diligence, endurance, and storage. Contains Earth (Ji), Metal (Xin), and Water (Gui). A storehouse of Metal.', 
    zh: '阴土（丑牛）：隆冬，冻土。象征勤劳、忍耐和收藏。藏干为己、辛、癸。为金库，性格稳重但有时固执。' 
  },
  '寅': { 
    en: 'Yang Wood (Tiger): Early Spring, New Growth. Represents vitality, boldness, and beginnings. Contains Wood (Jia), Fire (Bing), and Earth (Wu). The start of the Fire cycle.', 
    zh: '阳木（寅虎）：孟春，萌芽。象征生机、威严和开创。藏干为甲、丙、戊。为火的长生之地，充满爆发力。' 
  },
  '卯': { 
    en: 'Yin Wood (Rabbit): Mid-Spring, Lush Vegetation. Represents gentleness, detail, and networking. Contains pure Yin Wood (Yi). Associated with romance and negotiation.', 
    zh: '阴木（卯兔）：仲春，茂盛。象征细腻、温和和交际。藏干为乙木。为桃花之地，心思细密。' 
  },
  '辰': { 
    en: 'Yang Earth (Dragon): Late Spring, Reservoir. Represents transformation, ambition, and complexity. Contains Earth (Wu), Wood (Yi), and Water (Gui). A storehouse of Water.', 
    zh: '阳土（辰龙）：季春，水库。象征变化、雄心和复杂。藏干为戊、乙、癸。为水库，性格多变，能力强。' 
  },
  '巳': { 
    en: 'Yin Fire (Snake): Early Summer, Rising Sun. Represents enthusiasm, change, and travel. Contains Fire (Bing), Earth (Wu), and Metal (Geng). The start of the Metal cycle.', 
    zh: '阴火（巳蛇）：孟夏，初升之阳。象征热情、变动和驿马。藏干为丙、戊、庚。为金的长生之地，外冷内热。' 
  },
  '午': { 
    en: 'Yang Fire (Horse): Mid-Summer, High Noon. Represents peak energy, passion, and visibility. Contains Fire (Ding) and Earth (Ji). Associated with charisma and impatience.', 
    zh: '阳火（午马）：仲夏，正午。象征极盛、热烈和显赫。藏干为丁、己。为桃花之地，性格奔放，直来直去。' 
  },
  '未': { 
    en: 'Yin Earth (Goat): Late Summer, Dry Soil. Represents patience, support, and storage. Contains Earth (Ji), Fire (Ding), and Wood (Yi). A storehouse of Wood.', 
    zh: '阴土（未羊）：季夏，燥土。象征温顺、包容和木库。藏干为己、丁、乙。为木库，外柔内刚，有韧性。' 
  },
  '申': { 
    en: 'Yang Metal (Monkey): Early Autumn, Harvest. Represents execution, agility, and transmission. Contains Metal (Geng), Water (Ren), and Earth (Wu). The start of the Water cycle.', 
    zh: '阳金（申猴）：孟秋，收获。象征执行、灵敏和传送。藏干为庚、壬、戊。为水的长生之地，聪明好动。' 
  },
  '酉': { 
    en: 'Yin Metal (Rooster): Mid-Autumn, Purity. Represents precision, beauty, and confidence. Contains pure Yin Metal (Xin). Associated with aesthetics and clarity.', 
    zh: '阴金（酉鸡）：仲秋，纯净。象征精确、美丽和自信。藏干为辛金。为桃花之地，注重仪表，追求完美。' 
  },
  '戌': { 
    en: 'Yang Earth (Dog): Late Autumn, Dry Earth. Represents loyalty, protection, and spirituality. Contains Earth (Wu), Metal (Xin), and Fire (Ding). A storehouse of Fire.', 
    zh: '阳土（戌狗）：季秋，火库。象征忠诚、守护和防卫。藏干为戊、辛、丁。为火库，重情重义，有宗教缘。' 
  },
  '亥': { 
    en: 'Yin Water (Pig): Early Winter, Lake. Represents abundance, wisdom, and gathering. Contains Water (Ren) and Wood (Jia). The start of the Wood cycle.', 
    zh: '阴水（亥猪）：孟冬，湖泊。象征丰足、智慧和汇聚。藏干为壬、甲。为木的长生之地，随和乐观。' 
  }
};

const PINYIN_MAP = {
  'Jia': '甲', 'Yi': '乙', 'Bing': '丙', 'Ding': '丁', 'Wù': '戊', 'Ji': '己', 'Geng': '庚', 'Xin': '辛', 'Ren': '壬', 'Gui': '癸',
  'Zi': '子', 'Chou': '丑', 'Yin': '寅', 'Mao': '卯', 'Chen': '辰', 'Si': '巳', 'Wǔ': '午', 'Wei': '未', 'Shen': '申', 'You': '酉', 'Xu': '戌', 'Hai': '亥'
};

const getElementMeaning = (val, lang) => {
  let key = val;
  if (PINYIN_MAP[val]) key = PINYIN_MAP[val];
  if (ELEMENT_MEANINGS[key]) {
    return ELEMENT_MEANINGS[key][lang];
  }
  return '';
};

const SHISHEN_MEANINGS = {
  '比肩': { en: 'Friend (Bi Jian): Represents peers, friends, colleagues, and siblings of the same gender. Symbolizes equality, cooperation, but also competition and stubbornness.', zh: '比肩：代表兄弟姐妹、朋友、同事、同辈。象征平等、合作，但也代表竞争、固执和分担。' },
  'Friend': { en: 'Friend (Bi Jian): Represents peers, friends, colleagues, and siblings of the same gender. Symbolizes equality, cooperation, but also competition and stubbornness.', zh: '比肩：代表兄弟姐妹、朋友、同事、同辈。象征平等、合作，但也代表竞争、固执和分担。' },
  
  '劫财': { en: 'Rob Wealth (Jie Cai): Represents opposite-gender siblings, competitors, and social networks. Symbolizes charisma and social skills, but also financial loss, jealousy, and impulsiveness.', zh: '劫财：代表异性手足、竞争对手、社交圈。象征魅力、社交能力，但也代表破财、嫉妒和冲动。' },
  'Rob Wealth': { en: 'Rob Wealth (Jie Cai): Represents opposite-gender siblings, competitors, and social networks. Symbolizes charisma and social skills, but also financial loss, jealousy, and impulsiveness.', zh: '劫财：代表异性手足、竞争对手、社交圈。象征魅力、社交能力，但也代表破财、嫉妒和冲动。' },

  '食神': { en: 'Eating God (Shi Shen): Represents creativity, expression, enjoyment, and children (for females). Symbolizes intelligence, talent, relaxation, and good food, but can be lazy or introverted.', zh: '食神：代表才华、表达、享受、子女（女命）。象征智慧、温和、口福和悠闲，但也可能懒散或内向。' },
  'Eating God': { en: 'Eating God (Shi Shen): Represents creativity, expression, enjoyment, and children (for females). Symbolizes intelligence, talent, relaxation, and good food, but can be lazy or introverted.', zh: '食神：代表才华、表达、享受、子女（女命）。象征智慧、温和、口福和悠闲，但也可能懒散或内向。' },

  '伤官': { en: 'Hurting Officer (Shang Guan): Represents rebellion, innovation, performance, and children (for females). Symbolizes brilliance, outspokenness, and challenging authority, but can be arrogant or disruptive.', zh: '伤官：代表叛逆、创新、表演、子女（女命）。象征才气纵横、直言不讳、挑战权威，但也可能傲慢或惹是生非。' },
  'Hurting Officer': { en: 'Hurting Officer (Shang Guan): Represents rebellion, innovation, performance, and children (for females). Symbolizes brilliance, outspokenness, and challenging authority, but can be arrogant or disruptive.', zh: '伤官：代表叛逆、创新、表演、子女（女命）。象征才气纵横、直言不讳、挑战权威，但也可能傲慢或惹是生非。' },

  '正财': { en: 'Direct Wealth (Zheng Cai): Represents salary, savings, hard-earned money, and wife (for males). Symbolizes diligence, thrift, realism, and responsibility, but can be stingy or conservative.', zh: '正财：代表薪水、积蓄、正当收入、妻子（男命）。象征勤劳、节俭、务实和责任感，但也可能吝啬或保守。' },
  'Direct Wealth': { en: 'Direct Wealth (Zheng Cai): Represents salary, savings, hard-earned money, and wife (for males). Symbolizes diligence, thrift, realism, and responsibility, but can be stingy or conservative.', zh: '正财：代表薪水、积蓄、正当收入、妻子（男命）。象征勤劳、节俭、务实和责任感，但也可能吝啬或保守。' },

  '偏财': { en: 'Indirect Wealth (Pian Cai): Represents windfall, investment, business income, father, and mistress (for males). Symbolizes generosity, risk-taking, and quick money, but can be wasteful or unstable.', zh: '偏财：代表横财、投资、经商收入、父亲、情人（男命）。象征慷慨、冒险、赚快钱，但也可能挥霍或不稳定。' },
  'Indirect Wealth': { en: 'Indirect Wealth (Pian Cai): Represents windfall, investment, business income, father, and mistress (for males). Symbolizes generosity, risk-taking, and quick money, but can be wasteful or unstable.', zh: '偏财：代表横财、投资、经商收入、父亲、情人（男命）。象征慷慨、冒险、赚快钱，但也可能挥霍或不稳定。' },

  '正官': { en: 'Direct Officer (Zheng Guan): Represents authority, laws, career, reputation, and husband (for females). Symbolizes discipline, integrity, and order, but can be rigid or conservative.', zh: '正官：代表权力、法律、事业、名声、丈夫（女命）。象征自律、正直、守规矩，但也可能刻板或保守。' },
  'Direct Officer': { en: 'Direct Officer (Zheng Guan): Represents authority, laws, career, reputation, and husband (for females). Symbolizes discipline, integrity, and order, but can be rigid or conservative.', zh: '正官：代表权力、法律、事业、名声、丈夫（女命）。象征自律、正直、守规矩，但也可能刻板或保守。' },

  '七杀': { en: 'Seven Killings (Qi Sha): Represents pressure, challenges, military, and lover (for females). Symbolizes courage, decisiveness, and authority, but can be aggressive, ruthless, or stressful.', zh: '七杀：代表压力、挑战、军警、情人（女命）。象征果断、威严、魄力，但也可能暴躁、无情或带来灾难。' },
  'Seven Killings': { en: 'Seven Killings (Qi Sha): Represents pressure, challenges, military, and lover (for females). Symbolizes courage, decisiveness, and authority, but can be aggressive, ruthless, or stressful.', zh: '七杀：代表压力、挑战、军警、情人（女命）。象征果断、威严、魄力，但也可能暴躁、无情或带来灾难。' },

  '正印': { en: 'Direct Resource (Zheng Yin): Represents knowledge, education, mother, and nobility. Symbolizes wisdom, kindness, and protection, but can be dependent or slow.', zh: '正印：代表知识、学历、母亲、贵人。象征智慧、慈悲、庇护，但也可能依赖心重或反应迟钝。' },
  'Direct Resource': { en: 'Direct Resource (Zheng Yin): Represents knowledge, education, mother, and nobility. Symbolizes wisdom, kindness, and protection, but can be dependent or slow.', zh: '正印：代表知识、学历、母亲、贵人。象征智慧、慈悲、庇护，但也可能依赖心重或反应迟钝。' },

  '偏印': { en: 'Indirect Resource (Pian Yin): Represents unconventional knowledge, intuition, stepmother, and solitude. Symbolizes creativity, insight, and uniqueness, but can be eccentric, lonely, or suspicious.', zh: '偏印（枭神）：代表偏门知识、直觉、继母、孤独。象征独特、领悟力强、神秘，但也可能孤僻、多疑或刻薄。' },
  'Indirect Resource': { en: 'Indirect Resource (Pian Yin): Represents unconventional knowledge, intuition, stepmother, and solitude. Symbolizes creativity, insight, and uniqueness, but can be eccentric, lonely, or suspicious.', zh: '偏印（枭神）：代表偏门知识、直觉、继母、孤独。象征独特、领悟力强、神秘，但也可能孤僻、多疑或刻薄。' },
};

const getShiShenMeaning = (val, lang) => {
  if (SHISHEN_MEANINGS[val]) {
    return SHISHEN_MEANINGS[val][lang];
  }
  return '';
};

const getShiShen = (dayMaster, targetElement, targetYinYang, language) => {
  if (!dayMaster || !targetElement || !targetYinYang) return '';
  
  const dayMasterElement = STEMS_WUXING[dayMaster];
  const dayMasterYinYang = STEMS_YINYANG[dayMaster];
  
  if (!dayMasterElement || !dayMasterYinYang) return '';

  const basicRelations = {
    wood: { wood: '比', fire: '食', earth: '财', metal: '官', water: '印' },
    fire: { wood: '印', fire: '比', earth: '食', metal: '财', water: '官' },
    earth: { wood: '官', fire: '印', earth: '比', metal: '食', water: '财' },
    metal: { wood: '财', fire: '官', earth: '印', metal: '比', water: '食' },
    water: { wood: '食', fire: '财', earth: '官', metal: '印', water: '比' }
  };

  const basicRelation = basicRelations[dayMasterElement]?.[targetElement];
  if (!basicRelation) return '';

  const isSameYinYang = dayMasterYinYang === targetYinYang;
  
  const shiShenMap = language === 'zh' ? {
    '比': isSameYinYang ? '比肩' : '劫财',
    '印': isSameYinYang ? '偏印' : '正印',
    '食': isSameYinYang ? '食神' : '伤官',
    '财': isSameYinYang ? '偏财' : '正财',
    '官': isSameYinYang ? '七杀' : '正官',
  } : {
    '比': isSameYinYang ? 'Friend' : 'Rob Wealth',
    '印': isSameYinYang ? 'Indirect Resource' : 'Direct Resource',
    '食': isSameYinYang ? 'Eating God' : 'Hurting Officer',
    '财': isSameYinYang ? 'Indirect Wealth' : 'Direct Wealth',
    '官': isSameYinYang ? 'Seven Killings' : 'Direct Officer',
  };

  return shiShenMap[basicRelation] || '';
};

const getHiddenStemsData = (dayMaster, branch, language) => {
  const hiddenStems = BRANCH_HIDDEN_STEMS[branch] || [];
  return hiddenStems.map(stem => {
    const element = STEMS_WUXING[stem];
    const yinYang = STEMS_YINYANG[stem];
    const shiShen = getShiShen(dayMaster, element, yinYang, language);
    return { stem, shiShen };
  });
};

const getWuxingImage = (char, isStem = true) => {
  const element = isStem ? STEMS_WUXING[char] : BRANCHES_WUXING[char];
  return element ? WUXING_IMAGES[element] : '';
};

const parsePillar = (pillarString) => {
  if (!pillarString || pillarString.length < 2) {
    return { stem: '', branch: '' };
  }
  if (pillarString.includes('(')) {
    const parenIndex = pillarString.indexOf('(');
    const pinyinPart = pillarString.substring(parenIndex + 1, pillarString.length - 1);
    const pinyinWords = pinyinPart.split(' ');
    const stemPinyin = pinyinWords[0];
    const branchPinyin = pinyinWords[1];
    return { stem: stemPinyin, branch: branchPinyin };
  } else if (pillarString.includes(' ')) {
    const parts = pillarString.split(' ');
    return { stem: parts[0], branch: parts[1] };
  } else {
    const stem = pillarString.charAt(0);
    const branch = pillarString.charAt(1);
    return { stem, branch };
  }
};

const WuxingImage = ({ src, alt, className = "" }) => {
  if (!src) return null;
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className={`wuxing-image ${className}`}
      style={{ width: '20px', height: '20px', verticalAlign: 'middle' }}
    />
  );
};

export default function TSTnBaziResult({ trueSolarTime, baziPillars, isLoading, language = "en", gender, viewMode = "all" }) {
  const [selectedDayun, setSelectedDayun] = useState(null);
  const [selectedLiunian, setSelectedLiunian] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);

  const { list: dayunList, startInfo: dayunStartInfo } = useMemo(() => {
    if (baziPillars && trueSolarTime && gender) {
      return computeDayun(baziPillars, gender, trueSolarTime, language);
    }
    return { list: [], startInfo: null };
  }, [baziPillars, trueSolarTime, gender, language]);

  useEffect(() => {
    if (dayunList && dayunList.length > 0) {
      const nowYear = DateTime.now().year;
      const currentDayun = dayunList.find(dy => nowYear >= dy.startYear && nowYear <= dy.endYear);
      if (currentDayun) {
        setSelectedDayun(currentDayun);
      }
    }
  }, [dayunList]);

  const currentYear = DateTime.now().year;
  const currentLiunian = useMemo(() => computeLiunian(currentYear, language), [currentYear, language]);

  const liunianList = useMemo(() => {
    if (!selectedDayun) return [];
    const list = [];
    for (let i = 0; i < 10; i++) {
      const year = selectedDayun.startYear + i;
      const pillar = computeLiunian(year, language);
      const age = selectedDayun.startAge + i;
      list.push({ year, pillar, age });
    }
    return list;
  }, [selectedDayun, language]);

  useEffect(() => {
    if (selectedDayun && liunianList.length > 0) {
      const nowYear = DateTime.now().year;
      const currentLn = liunianList.find(ln => ln.year === nowYear);
      if (currentLn) {
        setSelectedLiunian(currentLn);
      } else {
        setSelectedLiunian(liunianList[0]);
      }
    }
  }, [selectedDayun, liunianList]);

  // Only show loading if we don't have data yet
  if (isLoading && !baziPillars) {
    return (
      <div className="fortune-container">
        <div className="bazi-box" style={{ textAlign: 'center' }}>
          <p>{language === "zh" ? "🌞 正在计算命盘..." : "🌞 Calculating chart..."}</p>
          <div className="loading-spinner">✨</div>
        </div>
      </div>
    );
  }

  const localizedTST = (() => {
    if (!trueSolarTime) return null;
    
    // If it's already a DateTime object
    if (typeof trueSolarTime === 'object' && trueSolarTime.isValid) {
      return trueSolarTime.setLocale(language === 'zh' ? 'zh-CN' : 'en-US').toFormat('yyyy-MM-dd HH:mm');
    }

    // If it's a string, try parsing
    if (typeof trueSolarTime === 'string') {
      // Use setZone: true to preserve the offset from the string (e.g. +00:00)
      // This prevents conversion to the browser's local timezone.
      let dt = DateTime.fromISO(trueSolarTime, { setZone: true });
      if (!dt.isValid) {
        dt = DateTime.fromSQL(trueSolarTime);
      }
      
      if (dt.isValid) {
        return dt.setLocale(language === 'zh' ? 'zh-CN' : 'en-US').toFormat('yyyy-MM-dd HH:mm');
      }
      // Fallback to raw string if parsing fails
      return trueSolarTime;
    }
    
    return null;
  })();

  // Parse all pillars
  const yearPillar = baziPillars ? parsePillar(baziPillars.yearPillar) : { stem: '', branch: '' };
  const monthPillar = baziPillars ? parsePillar(baziPillars.monthPillar) : { stem: '', branch: '' };
  const dayPillar = baziPillars ? parsePillar(baziPillars.dayPillar) : { stem: '', branch: '' };
  const hourPillar = baziPillars ? parsePillar(baziPillars.hourPillar) : { stem: '', branch: '' };

  const hasData = baziPillars && localizedTST;

  return (
    <div className="fortune-container">
      
      {(!hasData || viewMode === 'all' || viewMode === 'bazi') && (
      <div className="bazi-box">
        {hasData ? (
          <>
                <div className="tst-display">
                  <strong>{language === "zh" ? "真太阳时：" : "True Solar Time: "}</strong>
                  {localizedTST}
                </div>
                <div className="bazi-pillars-table-container">
                  <table className="bazi-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>{language === "zh" ? "年柱" : "Year"}</th>
                        <th>{language === "zh" ? "月柱" : "Month"}</th>
                        <th>{language === "zh" ? "日柱" : "Day"}</th>
                        <th>{language === "zh" ? "时柱" : "Hour"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Row 1: Shi Shen for Stems */}
                      <tr className="shi-shen-row">
                        <td>{language === "zh" ? "主星" : "Main Star"}</td>
                        <td onClick={() => {
                          const val = getShiShen(dayPillar.stem, STEMS_WUXING[yearPillar.stem], STEMS_YINYANG[yearPillar.stem], language);
                          if(val) setSelectedElement({ val, type: 'shishen' });
                        }} style={{ cursor: 'pointer' }}>
                          {getShiShen(dayPillar.stem, STEMS_WUXING[yearPillar.stem], STEMS_YINYANG[yearPillar.stem], language)}
                        </td>
                        <td onClick={() => {
                          const val = getShiShen(dayPillar.stem, STEMS_WUXING[monthPillar.stem], STEMS_YINYANG[monthPillar.stem], language);
                          if(val) setSelectedElement({ val, type: 'shishen' });
                        }} style={{ cursor: 'pointer' }}>
                          {getShiShen(dayPillar.stem, STEMS_WUXING[monthPillar.stem], STEMS_YINYANG[monthPillar.stem], language)}
                        </td>
                        <td>
                          {language === "zh" ? "日主" : "Day Master"}
                          {gender && (
                            <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
                              {gender === 'male' 
                                ? (language === 'zh' ? '元男' : 'Yuan Nan')
                                : (language === 'zh' ? '元女' : 'Yuan Nv')
                              }
                            </div>
                          )}
                        </td>
                        <td onClick={() => {
                          const val = getShiShen(dayPillar.stem, STEMS_WUXING[hourPillar.stem], STEMS_YINYANG[hourPillar.stem], language);
                          if(val) setSelectedElement({ val, type: 'shishen' });
                        }} style={{ cursor: 'pointer' }}>
                          {getShiShen(dayPillar.stem, STEMS_WUXING[hourPillar.stem], STEMS_YINYANG[hourPillar.stem], language)}
                        </td>
                      </tr>

                  {/* Row 2: Stems */}
                  <tr className="stem-row">
                    <td>{language === "zh" ? "天干" : "Stem"}</td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: yearPillar.stem })} style={{ cursor: 'pointer' }}>
                        <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[yearPillar.stem]] || 'inherit', fontWeight: 'bold' }}>
                          {getStemName(yearPillar.stem, language)}
                        </span>
                        <WuxingImage src={getWuxingImage(yearPillar.stem, true)} alt={STEMS_WUXING[yearPillar.stem]} />
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: monthPillar.stem })} style={{ cursor: 'pointer' }}>
                        <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[monthPillar.stem]] || 'inherit', fontWeight: 'bold' }}>
                          {getStemName(monthPillar.stem, language)}
                        </span>
                        <WuxingImage src={getWuxingImage(monthPillar.stem, true)} alt={STEMS_WUXING[monthPillar.stem]} />
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell day-master" onClick={() => setSelectedElement({ val: dayPillar.stem })} style={{ cursor: 'pointer' }}>
                        <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[dayPillar.stem]] || 'inherit', fontWeight: 'bold' }}>
                          {getStemName(dayPillar.stem, language)}
                        </span>
                        <WuxingImage src={getWuxingImage(dayPillar.stem, true)} alt={STEMS_WUXING[dayPillar.stem]} />
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: hourPillar.stem })} style={{ cursor: 'pointer' }}>
                        <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[hourPillar.stem]] || 'inherit', fontWeight: 'bold' }}>
                          {getStemName(hourPillar.stem, language)}
                        </span>
                        <WuxingImage src={getWuxingImage(hourPillar.stem, true)} alt={STEMS_WUXING[hourPillar.stem]} />
                      </div>
                    </td>
                  </tr>

                  {/* Row 3: Branches */}
                  <tr className="branch-row">
                    <td>{language === "zh" ? "地支" : "Branch"}</td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: yearPillar.branch })} style={{ cursor: 'pointer' }}>
                        <WuxingImage src={getWuxingImage(yearPillar.branch, false)} alt={BRANCHES_WUXING[yearPillar.branch]} />
                        <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[yearPillar.branch]] || 'inherit', fontWeight: 'bold' }}>
                          {getBranchName(yearPillar.branch, language)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: monthPillar.branch })} style={{ cursor: 'pointer' }}>
                        <WuxingImage src={getWuxingImage(monthPillar.branch, false)} alt={BRANCHES_WUXING[monthPillar.branch]} />
                        <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[monthPillar.branch]] || 'inherit', fontWeight: 'bold' }}>
                          {getBranchName(monthPillar.branch, language)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: dayPillar.branch })} style={{ cursor: 'pointer' }}>
                        <WuxingImage src={getWuxingImage(dayPillar.branch, false)} alt={BRANCHES_WUXING[dayPillar.branch]} />
                        <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[dayPillar.branch]] || 'inherit', fontWeight: 'bold' }}>
                          {getBranchName(dayPillar.branch, language)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="pillar-cell" onClick={() => setSelectedElement({ val: hourPillar.branch })} style={{ cursor: 'pointer' }}>
                        <WuxingImage src={getWuxingImage(hourPillar.branch, false)} alt={BRANCHES_WUXING[hourPillar.branch]} />
                        <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[hourPillar.branch]] || 'inherit', fontWeight: 'bold' }}>
                          {getBranchName(hourPillar.branch, language)}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {/* Row 4: Hidden Stems */}
                  <tr className="hidden-stems-row">
                    <td>{language === "zh" ? "藏干" : "Hidden Stems"}</td>
                    <td>
                      <div className="hidden-stems" style={{ flexDirection: 'column' }}>
                        {getHiddenStemsData(dayPillar.stem, yearPillar.branch, language).map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '2px' }}>
                             <span onClick={() => setSelectedElement({ val: item.stem })} style={{ 
                               cursor: 'pointer', 
                               marginRight: '4px',
                               color: ELEMENT_COLORS[STEMS_WUXING[item.stem]] || 'inherit',
                               fontWeight: 'bold'
                             }}>
                               {getStemName(item.stem, language)}
                             </span>
                             <span onClick={() => setSelectedElement({ val: item.shiShen, type: 'shishen' })} style={{ cursor: 'pointer', fontSize: '0.8em', color: '#666' }}>
                               {item.shiShen}
                             </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="hidden-stems" style={{ flexDirection: 'column' }}>
                        {getHiddenStemsData(dayPillar.stem, monthPillar.branch, language).map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '2px' }}>
                             <span onClick={() => setSelectedElement({ val: item.stem })} style={{ 
                               cursor: 'pointer', 
                               marginRight: '4px',
                               color: ELEMENT_COLORS[STEMS_WUXING[item.stem]] || 'inherit',
                               fontWeight: 'bold'
                             }}>
                               {getStemName(item.stem, language)}
                             </span>
                             <span onClick={() => setSelectedElement({ val: item.shiShen, type: 'shishen' })} style={{ cursor: 'pointer', fontSize: '0.8em', color: '#666' }}>
                               {item.shiShen}
                             </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="hidden-stems" style={{ flexDirection: 'column' }}>
                        {getHiddenStemsData(dayPillar.stem, dayPillar.branch, language).map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '2px' }}>
                             <span onClick={() => setSelectedElement({ val: item.stem })} style={{ 
                               cursor: 'pointer', 
                               marginRight: '4px',
                               color: ELEMENT_COLORS[STEMS_WUXING[item.stem]] || 'inherit',
                               fontWeight: 'bold'
                             }}>
                               {getStemName(item.stem, language)}
                             </span>
                             <span onClick={() => setSelectedElement({ val: item.shiShen, type: 'shishen' })} style={{ cursor: 'pointer', fontSize: '0.8em', color: '#666' }}>
                               {item.shiShen}
                             </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="hidden-stems" style={{ flexDirection: 'column' }}>
                        {getHiddenStemsData(dayPillar.stem, hourPillar.branch, language).map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '2px' }}>
                             <span onClick={() => setSelectedElement({ val: item.stem })} style={{ 
                               cursor: 'pointer', 
                               marginRight: '4px',
                               color: ELEMENT_COLORS[STEMS_WUXING[item.stem]] || 'inherit',
                               fontWeight: 'bold'
                             }}>
                               {getStemName(item.stem, language)}
                             </span>
                             <span onClick={() => setSelectedElement({ val: item.shiShen, type: 'shishen' })} style={{ cursor: 'pointer', fontSize: '0.8em', color: '#666' }}>
                               {item.shiShen}
                             </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#666" }}>
            {language === "zh" ? "请输入完整信息以计算命盘。" : "Please enter full details to compute chart."}
          </p>
        )}
      </div>
      )}

      {/* Dayun Section */}
      {(viewMode === 'all' || viewMode === 'dayun') && hasData && dayunList && dayunList.length > 0 && (
        <div className="bazi-box" style={{ marginTop: '20px' }}>
          <div className="dayun-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: 0, borderLeft: '4px solid #d4af37', paddingLeft: '10px' }}>
                {language === "zh" ? "大运 (Dayun)" : "Major Luck Cycles (Dayun)"}
              </h4>
              <div className="current-liunian" style={{ fontSize: '0.9em', color: '#666', backgroundColor: '#fff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #eee' }}>
                {language === "zh" ? `当前流年 (${currentYear}): ` : `Current Year (${currentYear}): `}
                <span style={{ fontWeight: 'bold', color: '#333' }}>{currentLiunian}</span>
              </div>
            </div>

            {/* Dayun Start Info */}
            {dayunStartInfo && (
              <div className="dayun-start-info" style={{ 
                marginBottom: '15px', 
                padding: '10px', 
                backgroundColor: '#f0f7ff', 
                borderRadius: '6px', 
                fontSize: '0.9em',
                color: '#444',
                borderLeft: '4px solid #008afb'
              }}>
                {language === "zh" ? (
                  <>
                    <strong>起运时间：</strong> 出生后 {dayunStartInfo.years} 年 {dayunStartInfo.months} 个月 {dayunStartInfo.days} 天起运
                    <br/>
                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                      (公历日期: {DateTime.fromISO(dayunStartInfo.startDate).setLocale('zh-CN').toFormat('yyyy年MM月dd日')})
                    </span>
                  </>
                ) : (
                  <>
                    <strong>Dayun Starts:</strong> {dayunStartInfo.years} years, {dayunStartInfo.months} months, and {dayunStartInfo.days} days after birth
                    <br/>
                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                      (Gregorian Date: {DateTime.fromISO(dayunStartInfo.startDate).setLocale('en-US').toFormat('yyyy-MM-dd')})
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="dayun-scroll-container" style={{ overflowX: 'auto', paddingBottom: '10px' }}>
              <div className="dayun-list" style={{ display: 'flex', gap: '10px' }}>
                {dayunList.map((dy) => (
                  <div 
                    key={dy.index} 
                    className="dayun-item" 
                    onClick={() => setSelectedDayun(dy)}
                    style={{ 
                      border: selectedDayun?.index === dy.index ? '2px solid #d4af37' : '1px solid #eee', 
                      borderRadius: '8px', 
                      padding: '10px', 
                      minWidth: '100px',
                      textAlign: 'center',
                      backgroundColor: selectedDayun?.index === dy.index ? '#fff9e6' : '#f9f9f9',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div className="dayun-pillar" style={{ fontSize: '1em', fontWeight: 'bold', marginBottom: '5px' }}>
                      {(() => {
                        const { stem, branch } = parsePillar(dy.pillar);
                        return (
                          <>
                            <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[stem]] || '#333', marginRight: '2px' }}>{stem}</span>
                            <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[branch]] || '#333' }}>{branch}</span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="dayun-age" style={{ fontSize: '0.8em', color: '#666' }}>
                      {language === "zh" ? `${dy.startAge} - ${dy.endAge} 岁` : `Age ${dy.startAge} - ${dy.endAge}`}
                    </div>
                    <div className="dayun-year" style={{ fontSize: '0.8em', color: '#999' }}>
                      {dy.startYear} - {dy.endYear}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Liunian Section (Details for selected Dayun) */}
            {selectedDayun && (
              <div className="liunian-container" style={{ marginTop: '15px', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #eee' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1em' }}>
                  {language === "zh" ? (
                    <>
                      {(() => {
                        const { stem, branch } = parsePillar(selectedDayun.pillar);
                        return (
                          <>
                            <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[stem]] || '#333' }}>{stem}</span>
                            <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[branch]] || '#333' }}>{branch}</span>
                          </>
                        );
                      })()}
                      {` 大运流年 (${selectedDayun.startYear}-${selectedDayun.endYear})`}
                    </>
                  ) : (
                    <>
                      Annual Luck Cycles (Liu Nian) for{' '}
                      {(() => {
                        const { stem, branch } = parsePillar(selectedDayun.pillar);
                        return (
                          <>
                            <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[stem]] || '#333' }}>{stem}</span>
                            <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[branch]] || '#333' }}>{branch}</span>
                          </>
                        );
                      })()}
                      {` (${selectedDayun.startYear}-${selectedDayun.endYear})`}
                    </>
                  )}
                </h5>
                <div className="liunian-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                  {liunianList.map((ln) => (
                    <div 
                      key={ln.year} 
                      className="liunian-item" 
                      onClick={() => setSelectedLiunian(ln)}
                      style={{ 
                        textAlign: 'center', 
                        padding: '8px', 
                        backgroundColor: selectedLiunian?.year === ln.year ? '#fff9e6' : '#f5f5f5', 
                        border: selectedLiunian?.year === ln.year ? '2px solid #d4af37' : '1px solid #eee',
                        borderRadius: '6px',
                        fontSize: '1em',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {(() => {
                          const { stem, branch } = parsePillar(ln.pillar);
                          return (
                            <>
                              <span style={{ color: ELEMENT_COLORS[STEMS_WUXING[stem]] || '#333', marginRight: '2px' }}>{stem}</span>
                              <span style={{ color: ELEMENT_COLORS[BRANCHES_WUXING[branch]] || '#333' }}>{branch}</span>
                            </>
                          );
                        })()}
                      </div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>{ln.year}</div>
                      <div style={{ fontSize: '0.8em', color: '#999' }}>{language === "zh" ? `${ln.age} 岁` : `Age ${ln.age}`}</div>
                    </div>
                  ))}
                </div>

                {selectedLiunian && (
                  <div className="integrated-chart-container" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1em', textAlign: 'center' }}>
                      {language === "zh" ? "流年-大运-原局 整合盘" : "Integrated Chart (Annual - Major - Natal)"}
                    </h5>
                    <div className="bazi-pillars-table-container">
                      <table className="bazi-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th style={{ color: '#d4af37' }}>{language === "zh" ? "流年" : "Annual"}</th>
                            <th style={{ color: '#d4af37' }}>{language === "zh" ? "大运" : "Major"}</th>
                            <th>{language === "zh" ? "年柱" : "Year"}</th>
                            <th>{language === "zh" ? "月柱" : "Month"}</th>
                            <th>{language === "zh" ? "日柱" : "Day"}</th>
                            <th>{language === "zh" ? "时柱" : "Hour"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const lnP = parsePillar(selectedLiunian.pillar);
                            const dyP = parsePillar(selectedDayun.pillar);
                            
                            const renderCell = (stem, branch, isStemRow) => {
                                const val = isStemRow ? stem : branch;
                                const wuxing = isStemRow ? STEMS_WUXING[val] : BRANCHES_WUXING[val];
                                return (
                                  <div className="pillar-cell" onClick={() => setSelectedElement({ val })} style={{ cursor: 'pointer' }}>
                                    {isStemRow ? (
                                      <>
                                        <span style={{ color: ELEMENT_COLORS[wuxing] || 'inherit', fontWeight: 'bold' }}>
                                          {getStemName(val, language)}
                                        </span>
                                        <WuxingImage src={getWuxingImage(val, true)} alt={wuxing} />
                                      </>
                                    ) : (
                                      <>
                                        <WuxingImage src={getWuxingImage(val, false)} alt={wuxing} />
                                        <span style={{ color: ELEMENT_COLORS[wuxing] || 'inherit', fontWeight: 'bold' }}>
                                          {getBranchName(val, language)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                );
                            };

                            const renderShiShen = (targetStem) => {
                                const val = getShiShen(dayPillar.stem, STEMS_WUXING[targetStem], STEMS_YINYANG[targetStem], language);
                                return (
                                    <div onClick={() => {
                                      if(val) setSelectedElement({ val, type: 'shishen' });
                                    }} style={{ cursor: 'pointer' }}>
                                      {val}
                                    </div>
                                );
                            };

                            return (
                              <>
                                <tr className="shi-shen-row">
                                  <td>{language === "zh" ? "主星" : "Main Star"}</td>
                                  <td>{renderShiShen(lnP.stem)}</td>
                                  <td>{renderShiShen(dyP.stem)}</td>
                                  <td>{renderShiShen(yearPillar.stem)}</td>
                                  <td>{renderShiShen(monthPillar.stem)}</td>
                                  <td>
                                    {language === "zh" ? "日主" : "Day Master"}
                                  </td>
                                  <td>{renderShiShen(hourPillar.stem)}</td>
                                </tr>
                                <tr className="stem-row">
                                  <td>{language === "zh" ? "天干" : "Stem"}</td>
                                  <td>{renderCell(lnP.stem, null, true)}</td>
                                  <td>{renderCell(dyP.stem, null, true)}</td>
                                  <td>{renderCell(yearPillar.stem, null, true)}</td>
                                  <td>{renderCell(monthPillar.stem, null, true)}</td>
                                  <td>{renderCell(dayPillar.stem, null, true)}</td>
                                  <td>{renderCell(hourPillar.stem, null, true)}</td>
                                </tr>
                                <tr className="branch-row">
                                  <td>{language === "zh" ? "地支" : "Branch"}</td>
                                  <td>{renderCell(null, lnP.branch, false)}</td>
                                  <td>{renderCell(null, dyP.branch, false)}</td>
                                  <td>{renderCell(null, yearPillar.branch, false)}</td>
                                  <td>{renderCell(null, monthPillar.branch, false)}</td>
                                  <td>{renderCell(null, dayPillar.branch, false)}</td>
                                  <td>{renderCell(null, hourPillar.branch, false)}</td>
                                </tr>
                                <tr className="hidden-stems-row">
                                  <td>{language === "zh" ? "藏干" : "Hidden Stems"}</td>
                                  {[lnP.branch, dyP.branch, yearPillar.branch, monthPillar.branch, dayPillar.branch, hourPillar.branch].map((br, idx) => (
                                    <td key={idx}>
                                      <div className="hidden-stems" style={{ flexDirection: 'column' }}>
                                        {getHiddenStemsData(dayPillar.stem, br, language).map((item, i) => (
                                          <div key={i} style={{ marginBottom: '2px' }}>
                                             <span onClick={() => setSelectedElement({ val: item.stem })} style={{ 
                                               cursor: 'pointer', 
                                               marginRight: '4px',
                                               color: ELEMENT_COLORS[STEMS_WUXING[item.stem]] || 'inherit',
                                               fontWeight: 'bold'
                                             }}>
                                               {getStemName(item.stem, language)}
                                             </span>
                                             <span onClick={() => setSelectedElement({ val: item.shiShen, type: 'shishen' })} style={{ cursor: 'pointer', fontSize: '0.8em', color: '#666' }}>
                                               {item.shiShen}
                                             </span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedElement && (
        <div className="element-modal-overlay" onClick={() => setSelectedElement(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div className="element-modal" onClick={e => e.stopPropagation()} style={{
            backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '80%', width: '300px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h4 style={{ marginTop: 0, color: selectedElement.type === 'shishen' ? '#333' : (ELEMENT_COLORS[STEMS_WUXING[selectedElement.val] || BRANCHES_WUXING[selectedElement.val]]) }}>
              {selectedElement.type === 'shishen' ? selectedElement.val : (getStemName(selectedElement.val, language) || getBranchName(selectedElement.val, language))}
            </h4>
            <p>{selectedElement.type === 'shishen' ? getShiShenMeaning(selectedElement.val, language) : getElementMeaning(selectedElement.val, language)}</p>
            <button onClick={() => setSelectedElement(null)} style={{
              marginTop: '10px', padding: '5px 15px', backgroundColor: '#d4af37', border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}>
              {language === 'zh' ? '关闭' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}